const CHUNK_RELOAD_KEY = 'chunk-reload';
const CHUNK_RELOAD_TS_KEY = 'chunk-reload-ts';

/** Collect a string from unknown rejection / error values (Angular lazy routes, webpack/esbuild chunks). */
export function textFromUnknown(err: unknown): string {
  if (err == null) return '';
  if (typeof err === 'string') return err;
  if (err instanceof Error) return `${err.name} ${err.message}`;
  if (typeof err === 'object' && 'message' in err) {
    const m = (err as { message?: unknown }).message;
    if (typeof m === 'string') return m;
  }
  try {
    return String(err);
  } catch {
    return '';
  }
}

export function isChunkRelatedFailure(combinedText: string, err?: unknown): boolean {
  const t = `${combinedText} ${textFromUnknown(err)}`.toLowerCase();
  return (
    t.includes('failed to fetch dynamically imported module') ||
    t.includes('dynamically imported module') ||
    t.includes('importing a module script failed') ||
    t.includes('error loading dynamically imported module') ||
    t.includes('loading css chunk') ||
    t.includes('loading chunk') ||
    t.includes('chunkloaderror') ||
    t.includes('chunk load error')
  );
}

async function hardReloadWithCacheBust(): Promise<void> {
  // Clear worker/runtime caches first so index/chunks resolve to the latest deploy.
  if ('serviceWorker' in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    } catch {
      // Ignore and still continue with reload.
    }
  }

  if ('caches' in window) {
    try {
      const cacheKeys = await caches.keys();
      await Promise.all(cacheKeys.map((cacheKey) => caches.delete(cacheKey)));
    } catch {
      // Ignore and still continue with reload.
    }
  }

  const nextUrl = new URL(window.location.href);
  nextUrl.searchParams.set('__lp_chunk_reload', Date.now().toString());
  window.location.replace(nextUrl.toString());
}

/** One forced reload after deploy / stale chunk; guard avoids infinite loops if CDN still broken. */
export function maybeReloadOnce(source: string, combinedText: string, err?: unknown): boolean {
  if (!isChunkRelatedFailure(combinedText, err)) return false;

  console.warn(`[LaunchPocket] Chunk or lazy-module load failed (${source}). Reloading once...`, combinedText);

  if (!sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, 'true');
    sessionStorage.setItem(CHUNK_RELOAD_TS_KEY, Date.now().toString());
    void hardReloadWithCacheBust();
  } else {
    console.error('[LaunchPocket] Chunk reload already attempted; not reloading again.');
  }

  return true;
}

export function clearChunkReloadGuard(): void {
  sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  sessionStorage.removeItem(CHUNK_RELOAD_TS_KEY);
}
